import React, { useState, useEffect } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Typography,
	Link,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	DialogActions,
	Grid,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { obtenerPedidosPendientesPago } from 'scripts/obtenerPedidosPendientesPago';
import { metodosPago, pedidoEstados } from 'utils/enums';
import { toast } from 'react-toastify';
import { crearPago } from 'scripts/crearPago';
import CustomDialog from 'helpers/Dialog';
import { cancelarPedido } from 'scripts/cancelarPedido';
import { obtenerTelefonoWhatsAppUruguay } from 'utils/userUtils';
import { formatFechaEntrega } from 'utils/dateUtils';

const OrderManagement = () => {
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [pedidoOpen, setPedidoOpen] = useState(null);
	const [filtroImporteMin, setFiltroImporteMin] = useState('');
	const [filtroImporteMax, setFiltroImporteMax] = useState('');
	const [filtroFechaEntrega, setFiltroFechaEntrega] = useState('');
	const [pago, setPago] = useState({
		pedido_id: '',
		metodo_pago_id: '',
		referencia: '',
		monto: '',
		fecha_pago: new Date().toISOString().split('T')[0], // Fecha de hoy en formato YYYY-MM-DD,
		observaciones: '',
	});

	// Fetch de datos (pedidos)
	const fetchPedidos = async () => {
		obtenerPedidosPendientesPago(dispatch, sessionStorage.getItem('token')).catch((e) => {
			toast.error(e.message);
		});
	};

	useEffect(() => {
		try {
			fetchPedidos();
		} catch (e) {
			console.error(e);
		}
	}, [dispatch]);

	const orders = useSelector((state) => state.pedidos.pedidos || []);
	const pedidosPendientes = orders.filter((order) => order.estado_pago === 'PENDIENTE');

	const calcularTotalPedido = (order) => {
		const totalGuardado = Number(order?.total);
		if (!Number.isNaN(totalGuardado)) {
			return totalGuardado;
		}
		if (!order?.detalles || order.detalles.length === 0) {
			return Number(order?.total || 0);
		}
		return order.detalles.reduce((acc, detalle) => acc + Number(detalle?.subtotal || 0), 0);
	};

	const obtenerMontoPagado = (order) => {
		if (order?.monto_pagado === null || order?.monto_pagado === undefined) {
			return null;
		}
		const monto = Number(order.monto_pagado);
		return Number.isNaN(monto) ? null : monto;
	};

	const getEstado = (order) => {
		if (order.estado === 3 && order.retiro_local) {
			return 'LISTO_PARA_RETIRAR';
		} else {
			return pedidoEstados[order.estado];
		}
	};

	const obtenerFechaEntregaISO = (fechaEntrega) => {
		if (!fechaEntrega) {
			return '';
		}

		return String(fechaEntrega).split('T')[0];
	};

	const handleLimpiarFiltros = () => {
		setFiltroImporteMin('');
		setFiltroImporteMax('');
		setFiltroFechaEntrega('');
	};

	const handleOpen = (order = null) => {
		setPedidoOpen(order);
		const montoDefault = Number(order?.total || 0);
		setPago({
			...pago,
			metodo_pago_id: order.metodo_pago,
			pedido_id: order.id,
			monto: montoDefault.toFixed(2),
		});
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setPedidoOpen(null);
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setPago({ ...pago, [name]: value });
	};

	const handleSaveOrder = () => {
		crearPago(pago, sessionStorage.getItem('token'))
			.then((data) => {
				toast.success('Pago registrado exitosamente.');
				fetchPedidos();
			})
			.catch((e) => {
				toast.error('Error al registrar el pago: ' + e.message);
			});
		handleClose();
	};

	const renderPaymentMethods = () => {
		if (pedidoOpen?.retiro_local) {
			return metodosPago.map((metodo) => (
				<option key={metodo.id} value={metodo.id}>
					{metodo.nombre}
				</option>
			));
		} else {
			return metodosPago.map(
				(metodo) =>
					metodo.id !== 3 && (
						<option key={metodo.id} value={metodo.id}>
							{metodo.nombre}
						</option>
					),
			);
		}
	};

	const handleOpenDialog = (id) => {
		setPedidoOpen(id);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setPedidoOpen(null);
		setDialogOpen(false);
	};

	const handleConfirmDialog = async () => {
		cancelarPedido(pedidoOpen, sessionStorage.getItem('token'))
			.then((data) => {
				toast.success(data.message);
				fetchPedidos();
			})
			.catch((e) => {
				toast.error(e.error);
			});
		setPedidoOpen(null);
		setDialogOpen(false);
	};

	const renderTelefonoCliente = (telefono) => {
		const telefonoWhatsapp = obtenerTelefonoWhatsAppUruguay(telefono);
		if (!telefonoWhatsapp) {
			return (
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					{telefono}
				</Typography>
			);
		}

		return (
			<Link
				href={`https://wa.me/${telefonoWhatsapp}`}
				target="_blank"
				rel="noopener noreferrer"
				underline="hover"
				sx={{ fontSize: '0.875rem' }}
			>
				{telefono}
			</Link>
		);
	};

	const esPedidoAdmin = (order) =>
		Boolean(order?.es_pedido_admin) ||
		String(order?.cliente_nombre || '').trim().toLowerCase() === 'cliente generico';

	const obtenerTextoCliente = (order) => {
		if (esPedidoAdmin(order)) {
			const notasPedido = String(order?.notas || '').trim();
			return notasPedido || 'Sin notas';
		}
		return order?.cliente_nombre;
	};

	const filteredPedidosPendientes = pedidosPendientes.filter((order) => {
		const totalPedido = calcularTotalPedido(order);
		const fechaEntrega = obtenerFechaEntregaISO(order.fecha_entrega);

		const matchesImporteMin = filtroImporteMin === '' || totalPedido >= Number(filtroImporteMin);
		const matchesImporteMax = filtroImporteMax === '' || totalPedido <= Number(filtroImporteMax);
		const matchesFechaEntrega = !filtroFechaEntrega || fechaEntrega === filtroFechaEntrega;

		return matchesImporteMin && matchesImporteMax && matchesFechaEntrega;
	});

	return (
		<div style={{ marginTop: '20px' }}>
			<Box
				sx={{
					marginBottom: 2,
					paddingBottom: 2,
					borderBottom: '2px solid #ccc',
				}}
			>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							label="Importe mínimo"
							type="number"
							variant="outlined"
							fullWidth
							value={filtroImporteMin}
							onChange={(e) => setFiltroImporteMin(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							label="Importe máximo"
							type="number"
							variant="outlined"
							fullWidth
							value={filtroImporteMax}
							onChange={(e) => setFiltroImporteMax(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							label="Fecha de entrega"
							type="date"
							variant="outlined"
							fullWidth
							InputLabelProps={{ shrink: true }}
							value={filtroFechaEntrega}
							onChange={(e) => setFiltroFechaEntrega(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Button
							variant="outlined"
							color="secondary"
							fullWidth
							sx={{ height: 56 }}
							onClick={handleLimpiarFiltros}
						>
							Limpiar filtros
						</Button>
					</Grid>
				</Grid>
			</Box>
			{/* Tabla de pedidos */}
			<TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
				<Table>
					<TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#FFFFFF' }}>
						<TableRow>
							<TableCell>Numero de pedido</TableCell>
							<TableCell>Estado</TableCell>
							<TableCell>Pago</TableCell>
							<TableCell>Total</TableCell>
							<TableCell>Monto pagado</TableCell>
							<TableCell>Notas del Cadete</TableCell>
							<TableCell sx={{ whiteSpace: 'nowrap' }}>Fecha de entrega</TableCell>
							<TableCell>Cliente</TableCell>
							<TableCell>Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredPedidosPendientes.length > 0 ? (
							filteredPedidosPendientes.map((order) => (
								<TableRow key={order.id}>
									<TableCell>{order.id}</TableCell>
									<TableCell>{getEstado(order)}</TableCell>
									<TableCell>
										{metodosPago[order.metodo_pago - 1].nombre + '\n' + order.estado_pago}
									</TableCell>
									<TableCell>${calcularTotalPedido(order).toFixed(2)}</TableCell>
									<TableCell>
										{obtenerMontoPagado(order) !== null ?
											`$${obtenerMontoPagado(order).toFixed(2)}`
										:	'Pendiente'}
									</TableCell>
									<TableCell>{order.notas_cadete}</TableCell>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>
										{formatFechaEntrega(order.fecha_entrega)}
									</TableCell>
									<TableCell>
										<Box sx={{ display: 'flex', flexDirection: 'column' }}>
											<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
												{obtenerTextoCliente(order)}
											</Typography>
											{!esPedidoAdmin(order) &&
												order.cliente_telefono &&
												renderTelefonoCliente(order.cliente_telefono)}
										</Box>
									</TableCell>
									<TableCell>
										<Button
											variant="outlined"
											color="primary"
											onClick={() => handleOpen(order)}
											sx={{ marginRight: 2 }}
										>
											Ingresar Pago
										</Button>
										<Button
											variant="outlined"
											color="secondary"
											onClick={() => handleOpenDialog(order.id)}
											sx={{ marginRight: 2 }}
										>
											Cancelar
										</Button>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={9} style={{ textAlign: 'center' }}>
									No hay pedidos disponibles.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>Ingresar Pago</DialogTitle>
				<DialogContent>
					Método de pago
					<TextField
						name="metodo_pago_id"
						variant="outlined"
						value={pago.metodo_pago_id}
						onChange={handleInputChange}
						fullWidth
						select
						SelectProps={{
							native: true,
						}}
						style={{ marginBottom: '10px' }}
					>
						<option value="">Seleccionar método de pago</option>
						{renderPaymentMethods()}
					</TextField>
					Monto
					<TextField
						name="monto"
						type="number"
						value={pago.monto}
						onChange={handleInputChange}
						fullWidth
						sx={{ marginBottom: 2 }}
					/>
					Fecha del Pago
					<TextField
						name="fecha_pago"
						type="date"
						value={pago.fecha_pago}
						onChange={handleInputChange}
						fullWidth
						sx={{ marginBottom: 2 }}
					/>
					Referencia
					<TextField
						name="referencia"
						value={pago.referencia}
						onChange={handleInputChange}
						fullWidth
						sx={{ marginBottom: 2 }}
					/>
					Observaciones
					<TextField
						name="observaciones"
						value={pago.observaciones}
						onChange={handleInputChange}
						fullWidth
						sx={{ marginBottom: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="secondary">
						Cancelar
					</Button>
					<Button onClick={handleSaveOrder} color="primary">
						Crear Pago
					</Button>
				</DialogActions>
			</Dialog>
			<CustomDialog
				open={dialogOpen}
				title="Confirmar cancelación"
				content="¿Está seguro de que desea cancelar este pedido?"
				onClose={handleCloseDialog}
				onConfirm={handleConfirmDialog}
			/>
		</div>
	);
};

export default OrderManagement;
